// -- [Interactive Drivible wordmark: hover wiggle, hover delay or click reveals driving car — hero + footer]

import { useCallback, useEffect, useRef, useState } from 'react'
import { DrivibleWordmarkHeroSvg } from './DrivibleWordmarkHeroSvg'
import { BRAND_TAGLINE } from './Logo'

const HOVER_REVEAL_MS = 500

/** -- Approximate center of the “d” counter (viewBox 0 0 1000 310) for wiggle pivot */
const WIGGLE_ORIGIN = '6.2% 49%'

export type DrivibleAnimatedLogoPlacement = 'hero' | 'footer'

interface DrivibleAnimatedLogoProps {
  className?: string
  /** -- [Hero: light focus ring on gradient. Footer: dark ring on gray-100.] */
  placement?: DrivibleAnimatedLogoPlacement
}

export function DrivibleAnimatedLogo({ className = '', placement = 'hero' }: DrivibleAnimatedLogoProps) {
  const [pointerInside, setPointerInside] = useState(false)
  const [lockedDriving, setLockedDriving] = useState(false)
  const [hoverRevealCar, setHoverRevealCar] = useState(false)
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const showCar = lockedDriving || hoverRevealCar

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current != null) {
      clearTimeout(hoverTimerRef.current)
      hoverTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => clearHoverTimer()
  }, [clearHoverTimer])

  const scheduleHoverCar = useCallback(() => {
    clearHoverTimer()
    hoverTimerRef.current = setTimeout(() => setHoverRevealCar(true), HOVER_REVEAL_MS)
  }, [clearHoverTimer])

  const onPointerEnter = () => {
    setPointerInside(true)
    if (!lockedDriving) scheduleHoverCar()
  }

  const onPointerLeave = () => {
    setPointerInside(false)
    clearHoverTimer()
    if (!lockedDriving) setHoverRevealCar(false)
  }

  const onClick = () => {
    setLockedDriving((was) => {
      if (!was) {
        clearHoverTimer()
        setHoverRevealCar(false)
        return true
      }
      clearHoverTimer()
      if (pointerInside) scheduleHoverCar()
      else setHoverRevealCar(false)
      return false
    })
  }

  const wiggle = pointerInside && !showCar

  const focusRingClass =
    placement === 'footer'
      ? 'focus-visible:outline-gray-900'
      : 'focus-visible:outline-white'

  const umamiEvent = placement === 'footer' ? 'Footer - Animated logo' : 'Hero - Animated logo'

  return (
    <button
      type="button"
      className={`relative block h-12 w-[10.3125rem] shrink-0 cursor-pointer select-none border-0 bg-transparent p-0 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${focusRingClass} ${className}`}
      title={BRAND_TAGLINE}
      aria-label="Drivible logo. Press to toggle driving animation."
      aria-pressed={lockedDriving}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onClick={onClick}
      data-umami-event={umamiEvent}
    >
      <span className="pointer-events-none relative block h-12 w-full overflow-visible">
        <span
          className={`absolute inset-0 flex items-center transition-opacity duration-300 ease-out ${
            showCar ? 'opacity-0' : 'opacity-100'
          }`}
          aria-hidden={showCar}
        >
          <span
            className={`inline-block text-[#1a1a1a] ${wiggle ? 'animate-hero-logo-wiggle' : ''}`}
            style={{ transformOrigin: WIGGLE_ORIGIN }}
          >
            <DrivibleWordmarkHeroSvg className="h-12 w-auto max-w-none" />
          </span>
        </span>

        <span
          className={`absolute inset-0 flex flex-col justify-end transition-opacity duration-300 ease-out ${
            showCar ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
          aria-hidden={!showCar}
        >
          <span className="relative block h-full w-full overflow-hidden rounded-sm">
            {/* -- [Road: stripe layer is wider + offset left so translateX(0→32px) never uncovers a static bg] */}
            <span className="absolute bottom-0 left-0 right-0 h-[42%] overflow-hidden bg-[#4b5563]">
              <span
                className="animate-hero-road-scroll absolute inset-y-0 -left-8 w-[calc(100%+4rem)]"
                style={{
                  backgroundImage:
                    'repeating-linear-gradient(90deg, #4b5563 0px, #4b5563 16px, #9ca3af 16px, #9ca3af 32px)',
                }}
              />
            </span>
            <span className="animate-hero-drive-bounce relative z-10 flex h-full w-full items-end justify-center pb-px">
              <span className="relative inline-block h-[2.475rem] w-[7.875rem]">
                <img
                  src="/logos/speedy-wheels.svg"
                  alt="Drivible driving car mascot"
                  width={3800}
                  height={1400}
                  decoding="async"
                  className="relative z-10 mx-auto h-[2.175rem] w-auto object-contain object-bottom"
                />
              </span>
            </span>
          </span>
        </span>
      </span>
    </button>
  )
}
